import { useId, type FC, type ReactNode } from 'react';

import { cn } from './cn.js';

export interface TooltipProps {
  /** Содержимое подсказки - пояснение, без кнопок и ссылок. */
  content: ReactNode;
  /** Триггер - текст или иконка. */
  children: ReactNode;
  /** К какому краю триггера прижать подсказку. */
  align?: 'start' | 'end';
  /**
   * Подпись триггера для программ чтения экрана - только у триггера без текста
   * (иконка): она заменяет текст триггера, а подсказка и так читается описанием.
   */
  label?: string;
  className?: string;
}

/**
 * Всплывающая подсказка без библиотеки: показывается при наведении и при фокусе
 * на триггере - с клавиатуры и касанием на телефоне (кнопка получает фокус).
 * Только пояснение: интерактива внутри нет, и то же самое должно быть видно
 * без наведения - на вкладке или в списке.
 */
export const Tooltip: FC<TooltipProps> = ({
  content,
  children,
  align = 'start',
  label,
  className,
}) => {
  const id = useId();

  return (
    <span className={cn('group relative inline-flex', className)}>
      <button
        type="button"
        aria-describedby={id}
        aria-label={label}
        className="inline-flex cursor-help items-center gap-1 rounded-sm bg-transparent p-0 text-left text-inherit outline-none [font:inherit] focus-visible:ring-2 focus-visible:ring-brand-green-100"
      >
        {children}
      </button>
      <span
        role="tooltip"
        id={id}
        className={cn(
          'pointer-events-none invisible absolute top-full z-30 mt-1.5 w-max max-w-[min(360px,calc(100vw-32px))] rounded-md bg-ink-900 px-3 py-2.5 text-left text-[12px] leading-[1.45] font-normal text-white opacity-0 shadow-lg transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100',
          align === 'start' ? 'left-0' : 'right-0',
        )}
      >
        {content}
      </span>
    </span>
  );
};
